import {
  PatientSyncJob,
  PatientSyncJobMetadataProgress,
  PatientSyncJobPhase,
  PatientSyncJobStatus,
  useGetJobById,
} from "@elation/api-client/src/stitch-web";
import { useEffect, useState } from "react";
import { useAppBusEvent } from "./useAppBusEvent";
import { useAppSSEEventsFallbackPolling } from "./useAppSSEEventsFallbackPolling";

const FALLBACK_POLL_INTERVAL_MS = 1_000;

const NO_JOB_ID = "no-patient-sync-job-id";

function isActiveStatus(status: PatientSyncJobStatus | undefined): boolean {
  return status === "pending" || status === "running";
}

function toState(job: PatientSyncJob): PatientSyncJobState {
  return {
    jobId: job.jobId,
    phase: job.metadata.phase,
    status: job.status,
    progress: job.metadata.progress,
    ...(job.error != null ? { error: job.error } : {}),
  };
}

/**
 * Live patient sync job state derived from the app SSE stream.
 *
 * We own this in component state instead of the React Query cache because
 * `useAppSSEEvents` is the single source of truth for it.
 */
export interface PatientSyncJobState {
  jobId: string;
  phase: PatientSyncJobPhase;
  status: PatientSyncJobStatus;
  progress: PatientSyncJobMetadataProgress;
  error?: string;
}

const INITIAL_PROGRESS: PatientSyncJobMetadataProgress = {
  total: 0,
  completed: 0,
  failed: 0,
  succeeded: 0,
};

export function usePatientSyncJob({
  enabled,
  initialPatientSyncJobId,
}: {
  enabled: boolean;
  initialPatientSyncJobId?: string | null;
}) {
  const [patientSyncJob, setPatientSyncJob] =
    useState<PatientSyncJobState | null>(null);

  const { isFallbackPolling } = useAppSSEEventsFallbackPolling();

  // The job id we want to fetch is whichever we have a handle on:
  //   - the live SSE-derived job (so we can re-fetch its terminal state if
  //     SSE drops mid-sync), or
  //   - the seed id passed in via `initialPatientSyncJobId` (so an already-
  //     running sync hydrates without waiting for a fresh started event).
  const trackedJobId = patientSyncJob?.jobId ?? initialPatientSyncJobId;

  // Seed-fetch enables once when we have a tracked id and no live state yet.
  // Fallback-poll enables when SSE has dropped to fallback mode AND the last
  // known status is still active — both clear once we observe a terminal
  // status, so we never poll forever.
  const needsSeed = trackedJobId != null && patientSyncJob == null;
  const isStillActive = isActiveStatus(patientSyncJob?.status);
  const shouldFetchJob =
    enabled &&
    trackedJobId != null &&
    (needsSeed || (isFallbackPolling && isStillActive));

  const { data: jobData } = useGetJobById(trackedJobId ?? NO_JOB_ID, {
    query: {
      enabled: shouldFetchJob,
      refetchInterval:
        isFallbackPolling && isStillActive ? FALLBACK_POLL_INTERVAL_MS : false,
    },
  });

  useAppBusEvent("physician.patient_sync_started", ({ jobId }) => {
    setPatientSyncJob({
      jobId,
      status: "running",
      phase: "fetching-patients",
      progress: INITIAL_PROGRESS,
    });
  });

  useAppBusEvent(
    "physician.patient_sync_progress",
    ({ jobId, phase, progress }) => {
      setPatientSyncJob((prev) => {
        if (
          prev != null &&
          prev.jobId === jobId &&
          progress.completed <= prev.progress.completed
        ) {
          return prev;
        }

        return prev?.jobId === jobId
          ? { ...prev, status: "running", phase, progress }
          : { jobId, status: "running", phase, progress };
      });
    },
  );

  useAppBusEvent("physician.patient_sync_succeeded", ({ jobId }) => {
    setPatientSyncJob((prev) =>
      prev?.jobId === jobId ? { ...prev, status: "completed" } : prev,
    );
  });

  useAppBusEvent("physician.patient_sync_failed", ({ jobId, error }) => {
    setPatientSyncJob((prev) =>
      prev?.jobId === jobId ? { ...prev, status: "failed", error } : prev,
    );
  });

  useEffect(() => {
    const fetched = jobData?.data as PatientSyncJob | undefined;
    if (!fetched || fetched.jobType !== "ehr_patient_sync_requested") return;
    if (patientSyncJob && patientSyncJob.jobId !== fetched.jobId) return;
    // Take the fetched snapshot whenever it differs from what we already
    // have. updatedAt is monotonic per server-side write, so we use a quick
    // status/progress equality check instead of trying to compare timestamps.
    if (
      patientSyncJob?.status === fetched.status &&
      patientSyncJob?.phase === fetched.metadata.phase &&
      patientSyncJob?.progress.completed ===
        fetched.metadata.progress.completed &&
      patientSyncJob?.progress.total === fetched.metadata.progress.total
    ) {
      return;
    }

    if (
      patientSyncJob != null &&
      fetched.metadata.progress.completed < patientSyncJob.progress.completed
    ) {
      return;
    }

    setPatientSyncJob(toState(fetched));
  }, [jobData, patientSyncJob]);

  return { patientSyncJob, setPatientSyncJob };
}
